import { type ReactElement } from 'react';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { type Arch, Platform } from '@/lib/platform';
import type { releases } from '@kirin/api-client/contracts';
import {
  InstallTrigger,
  type InstallEnvironment,
  type PlatformDownloadMeta,
} from '../InstallTrigger';

interface AgentInstallStepsProps {
  platformName: InstallEnvironment;
  platformDownloadMeta: PlatformDownloadMeta[];
  detectedPlatform: Platform | null;
  userArch?: Arch | null;
  /** KIR-4071: full manifest, forwarded to InstallTrigger for the checksums popout. */
  installerHashes?: releases.InstallerHashes | null;
}

/**
 * The install / configure / uninstall body for the Kirin Agent (single PKG/EXE,
 * same `kirin` CLI that covers Claude Code, Claude Desktop, Windsurf, …).
 * Rendered once inside the "Kirin Agent" setup frame on the Get Started screen.
 */
export function AgentInstallSteps({
  platformName,
  platformDownloadMeta,
  detectedPlatform,
  userArch,
  installerHashes,
}: AgentInstallStepsProps): ReactElement {
  // `?? false` is intentional: while flags are loading the value is
  // `undefined`, and we want the safe default (Linux unsupported) for that
  // transient state. Orgs with the flag ON may briefly see Linux entries
  // appear once flags resolve — that direction of flicker (entries appear) is
  // acceptable; the reverse (entries disappear after a click) is not.
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.LINUX_AGENT_RELEASE,
  ]);
  const linuxAgentReleased =
    flags[flagsmithFeatureFlags.LINUX_AGENT_RELEASE] ?? false;

  const gatedDownloadMeta = linuxAgentReleased
    ? platformDownloadMeta
    : platformDownloadMeta.filter(m => m.platform !== Platform.LINUX);

  const hasAnyUrl = gatedDownloadMeta.some(m => m.url);

  const effectivePlatform = detectedPlatform ?? Platform.DARWIN;

  // Version is identical across all entries; fall back to placeholder if unavailable.
  const agentVersion = platformDownloadMeta[0]?.version ?? 'VERSION';
  const debArch = userArch ?? 'amd64';
  const rpmArch = userArch === 'arm64' ? 'aarch64' : 'x86_64';

  return (
    <>
      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          Install Kirin Agent
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="font-medium">Download the installation package</div>
            {hasAnyUrl && (
              <InstallTrigger
                environment={platformName}
                userPlatform={effectivePlatform}
                userArch={userArch}
                platformDownloadMeta={gatedDownloadMeta}
                installerHashes={installerHashes}
                formatLabel={meta => {
                  if (meta.platform === Platform.WIN32) {
                    const arch = meta.arch ?? 'amd64';
                    return `Download Kirin for Windows ${arch} (Installer)`;
                  }
                  if (meta.platform === Platform.LINUX) {
                    const arch = meta.arch ?? 'amd64';
                    const fmt = meta.format ? ` (${meta.format})` : '';
                    return `Download Kirin for Linux ${arch}${fmt}`;
                  }
                  return 'Download Kirin for macOS (Installer)';
                }}
              />
            )}
            <div className="text-xs text-muted-foreground">
              {detectedPlatform === Platform.DARWIN && (
                <>
                  Double-click the PKG to install. The{' '}
                  <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                    kirin
                  </code>{' '}
                  command will be available in Terminal.
                </>
              )}
              {detectedPlatform === Platform.WIN32 && (
                <>
                  Run the installer and follow the prompts. The{' '}
                  <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                    kirin
                  </code>{' '}
                  command will be available in Command Prompt or PowerShell.
                </>
              )}
              {detectedPlatform === Platform.LINUX && (
                <>
                  Install with your package manager (e.g.{' '}
                  <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                    {`sudo apt install ./kirin_${agentVersion}_${debArch}.deb`}
                  </code>{' '}
                  or{' '}
                  <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                    {`sudo dnf install ./kirin-${agentVersion}.${rpmArch}.rpm`}
                  </code>
                  ).
                </>
              )}
              {detectedPlatform !== Platform.DARWIN &&
                detectedPlatform !== Platform.WIN32 &&
                detectedPlatform !== Platform.LINUX && (
                  <>
                    Choose the installer for your platform. Follow the included
                    instructions.
                  </>
                )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          Configure & Verify
        </h3>
        <div className="space-y-6">
          <div className="space-y-3">
            <div className="font-medium">Set Your API Key</div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>
                Open{' '}
                {detectedPlatform === Platform.WIN32
                  ? 'Command Prompt or PowerShell'
                  : 'Terminal'}
              </li>
              <li>
                Run{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  kirin config set api-key
                </code>
              </li>
              <li>Enter your API key when prompted</li>
              <li>
                Verify with{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  kirin status
                </code>
              </li>
            </ol>
          </div>

          <HighlightBox variant="info" padding="lg">
            <div className="text-sm">
              <div className="font-medium mb-2">What happens next?</div>
              <div className="text-muted-foreground">
                Kirin runs in the background and automatically scans files when
                they're created or modified. Check the dashboard to review
                findings and manage quarantined files.
              </div>
            </div>
          </HighlightBox>
        </div>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          To Uninstall:
        </h3>
        <ol className="list-decimal ml-5 text-sm space-y-2">
          <li>
            Open{' '}
            {detectedPlatform === Platform.WIN32
              ? 'Command Prompt or PowerShell'
              : 'Terminal'}
          </li>
          <li>
            Run{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              sudo kirin uninstall
            </code>
          </li>
          <li>Follow the prompts to complete the uninstallation</li>
        </ol>
      </div>
    </>
  );
}
